import React, { useState, useEffect, useRef } from 'react';
import { 
  FaArrowLeft, FaEdit, FaSave, FaUserGraduate, FaBuilding, 
  FaCalendarAlt, FaIdBadge, FaUserTie, FaPhoneAlt, FaEnvelope, 
  FaMapMarkerAlt, FaSchool, FaVenusMars, FaInfoCircle, FaCamera
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api, { BASE_URL } from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import { toTitleCase, INDIAN_STATES } from '../../../../utils/formHelpers';
import StateSelect from '../../../../components/common/StateSelect/StateSelect';
import './StudentDetails.css';

const API_BASE = BASE_URL;

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [classes, setClasses] = useState([]);
  const [editDuration, setEditDuration] = useState('');
  const [editMedium, setEditMedium] = useState('');
  
  // Original fetched data for read-only fields
  const [studentData, setStudentData] = useState(null);

  // Editable Form Data
  const [formData, setFormData] = useState({
    classId: '',
    name: '',
    dob: '',
    gender: '',
    category: '',
    religion: '',
    fatherName: '',
    fatherOccupation: '',
    fatherContact: '',
    alternativeContact: '',
    motherName: '',
    motherOccupation: '',
    contact: '',
    email: '',
    address: '',
    city: '',
    state: '',
    country: '',
    lastSchoolName: '',
    lastSchoolClass: '',
    lastSchoolResult: '',
    leftReason: ''
  });

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [educationDoc, setEducationDoc] = useState(null);
  const [educationDocName, setEducationDocName] = useState('');
  const [existingDocPath, setExistingDocPath] = useState('');

  // ── Fetch Student Data ──
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/students/${id}`)
      .then(res => {
        const s = res.data.data;
        setStudentData(s);
        setFormData({
          classId: s.class?._id || s.class || '',
          name: s.name || '',
          dob: s.dob ? s.dob.split('T')[0] : '',
          gender: s.gender || '',
          category: s.category || '',
          religion: s.religion || '',
          fatherName: s.fatherName || '',
          fatherOccupation: s.fatherOccupation || '',
          fatherContact: s.fatherContact || '',
          alternativeContact: s.alternativeContact || '',
          motherName: s.motherName || '',
          motherOccupation: s.motherOccupation || '',
          contact: s.contact || '',
          email: s.email || '',
          address: s.address || '',
          city: s.city || '',
          state: s.state || '',
          country: s.country || '',
          lastSchoolName: s.lastSchoolName || '',
          lastSchoolClass: s.lastSchoolClass || '',
          lastSchoolResult: s.lastSchoolResult || '',
          leftReason: s.leftReason || ''
        });

        if (s.photo) setPhotoPreview(`${API_BASE}/${s.photo}`);
        if (s.educationDocument) {
          setExistingDocPath(s.educationDocument);
          setEducationDocName(s.educationDocument.split('/').pop());
        }

        setEditDuration(s.class?.duration || '');
        setEditMedium(s.class?.medium || '');
        if (s.college?._id || s.college) {
          api.get('/classes', { params: { college: s.college?._id || s.college } })
            .then(cRes => setClasses(cRes.data.data || []))
            .catch(() => setClasses([]));
        }
      })
      .catch(err => {
        const msg = err.response?.data?.message || 'Failed to load student details.';
        Swal.fire('Error', msg, 'error');
        navigate(-1);
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const titleFields = ['name','fatherName','motherName','fatherOccupation','motherOccupation','address','city'];
    const phoneFields = ['contact','fatherContact','alternativeContact'];
    if (phoneFields.includes(name)) {
      let digits = value.replace(/\D/g, '');
      if (digits.length > 0 && !/^[5-9]/.test(digits)) digits = digits.slice(1);
      if (digits.length > 10) digits = digits.slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digits }));
      return;
    }
    if (name === 'classId') {
      const selected = classes.find(c => String(c._id) === String(value));
      setEditDuration(selected?.duration || '');
      setEditMedium(selected?.medium || '');
    }
    const finalValue = titleFields.includes(name) ? toTitleCase(value) : value;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleEducationDocChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        Swal.fire('Invalid File', 'Only PDF files are allowed for education documents.', 'error');
        e.target.value = '';
        return;
      }
      setEducationDoc(file);
      setEducationDocName(file.name);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    const confirm = await Swal.fire({
      title: 'Save Changes?',
      html: `Are you sure you want to update the details for <b>${studentData?.name}</b>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, Save Changes',
      cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(k => data.append(k, formData[k]));
      if (photo) data.append('photo', photo);
      if (educationDoc) data.append('educationDocument', educationDoc);

      await api.put(`/students/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Student details saved successfully.',
        confirmButtonColor: '#10b981',
        timer: 2000,
        timerProgressBar: true,
      });
      
      setIsEditing(false);
      const refresh = await api.get(`/students/${id}`);
      const s = refresh.data.data;
      setStudentData(s);
      if (s.photo) setPhotoPreview(`${API_BASE}/${s.photo}`);
      if (s.educationDocument) {
        setExistingDocPath(s.educationDocument);
        setEducationDocName(s.educationDocument.split('/').pop());
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to update student.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="student-details-loader">Loading Student Profile...</div>;
  }

  // Formatting read-only data
  const readOnlyCollege  = studentData?.college?.name || '—';
  const readOnlySession  = studentData?.session?.name || '—';
  const readOnlyBatch    = studentData?.admissionYear || studentData?.session?.name?.split('-')[0] || '—';
  const readOnlyAdmDate  = studentData?.admissionDate ? new Date(studentData.admissionDate).toLocaleDateString('en-GB') : '—';
  const readOnlyAdmNo    = studentData?.admissionNo || '—';
  const readOnlyClass    = isEditing
    ? (classes.find(c => String(c._id) === String(formData.classId))?.name || studentData?.class?.name || studentData?.className || '—')
    : (studentData?.class?.name || studentData?.className || '—');
  const _dur = isEditing ? editDuration : studentData?.class?.duration;
  const readOnlyDuration = _dur ? `${_dur} Year${parseInt(_dur) > 1 ? 's' : ''}` : '—';
  const readOnlyMedium   = isEditing ? (editMedium || '—') : (studentData?.class?.medium || studentData?.medium || '—');

  return (
    <div className="student-details-container">
      
      {/* ── Header ── */}
      <div className="student-details-header">
        <div className="student-details-header-left">
          <button type="button" className="student-details-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <h2 className="student-details-title">Student Details</h2>
        </div>
        {!isEditing ? (
          <button className="student-details-edit-btn" onClick={() => setIsEditing(true)}>
            <FaEdit /> Update Details
          </button>
        ) : (
          <button className="student-details-cancel-edit-btn" onClick={() => { setIsEditing(false); setPhoto(null); setEducationDoc(null); }}>
            Cancel Edit
          </button>
        )}
      </div>

      <form className="student-details-form" onSubmit={handleSubmit}>
        
        {/* ── Top Profile Card ── */}
        <div className="student-details-profile-card">

          {/* Student name spans full width at top */}
          <div className="student-details-name-banner">
            <FaUserGraduate className="student-details-name-icon" />
            <h3 className="student-details-display-name">{studentData?.name}</h3>
          </div>

          <div className="student-details-profile-body">
            {/* Photo */}
            <div className="student-details-photo-section">
              <div className={`student-details-photo-wrapper ${isEditing ? 'editing' : ''}`}>
                {photoPreview ? (
                  <img src={photoPreview} alt={studentData?.name} className="student-details-photo-img" />
                ) : (
                  <div className="student-details-photo-fallback"><FaUserGraduate /></div>
                )}
                {isEditing && (
                  <label className="student-details-photo-overlay" htmlFor="photo-upload">
                    <FaCamera />
                    <span>Change</span>
                  </label>
                )}
              </div>
              <input type="file" id="photo-upload" accept="image/*" onChange={handlePhotoChange} ref={fileInputRef} style={{ display: 'none' }} disabled={!isEditing} />
            </div>

            {/* Info grid */}
            <div className="student-details-restricted-info">
              <div className="student-details-restricted-grid">
                <div className="student-details-restricted-item student-details-college-item">
                  <span className="student-details-rest-label">College</span>
                  <span className="student-details-rest-val college-badge" title={readOnlyCollege}>
                    <FaBuilding className="student-details-rest-icon" />
                    <span className="student-details-college-name">{readOnlyCollege}</span>
                  </span>
                </div>
                <div className="student-details-restricted-item">
                  <span className="student-details-rest-label">Admission No</span>
                  <span className="student-details-rest-val adm-badge"><FaIdBadge /> {readOnlyAdmNo}</span>
                </div>
                <div className="student-details-restricted-item">
                  <span className="student-details-rest-label">Session</span>
                  <span className="student-details-rest-val">{readOnlySession}</span>
                </div>
                <div className="student-details-restricted-item">
                  <span className="student-details-rest-label">Batch</span>
                  <span className="student-details-rest-val">{readOnlyBatch}</span>
                </div>
                <div className="student-details-restricted-item">
                  <span className="student-details-rest-label">Admission Date</span>
                  <span className="student-details-rest-val"><FaCalendarAlt /> {readOnlyAdmDate}</span>
                </div>
                <div className="student-details-restricted-item">
                  <span className="student-details-rest-label">Class</span>
                  <span className="student-details-rest-val">{readOnlyClass}</span>
                </div>
                <div className="student-details-restricted-item">
                  <span className="student-details-rest-label">Program Duration</span>
                  <span className="student-details-rest-val">{readOnlyDuration}</span>
                </div>
                <div className="student-details-restricted-item">
                  <span className="student-details-rest-label">Medium</span>
                  <span className="student-details-rest-val">
                    <span className="student-details-medium-badge">{readOnlyMedium}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── SECTION: Personal Information ── */}
        <div className="student-details-section">
          <h3 className="student-details-section-title"><FaUserGraduate /> Personal Information</h3>
          <div className="student-details-grid student-details-grid-4">
            
            <div className="student-details-group">
              <label className="student-details-label">Full Name <span className="student-details-req">*</span></label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className="student-details-input" readOnly={!isEditing} required={isEditing} />
            </div>

            <div className="student-details-group">
              <label className="student-details-label">Date of Birth <span className="student-details-req">*</span></label>
              <input type="date" name="dob" value={formData.dob} onChange={handleChange} className="student-details-input" readOnly={!isEditing} required={isEditing} />
            </div>

            <div className="student-details-group">
              <label className="student-details-label">Gender <span className="student-details-req">*</span></label>
              {isEditing ? (
                <select name="gender" value={formData.gender} onChange={handleChange} className="student-details-input" required>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Transgender">Transgender</option>
                  <option value="Other">Other</option>
                </select>
              ) : (
                <input type="text" value={formData.gender || '—'} className="student-details-input" readOnly />
              )}
            </div>

            <div className="student-details-group">
              <label className="student-details-label">Category</label>
              {isEditing ? (
                <select name="category" value={formData.category} onChange={handleChange} className="student-details-input">
                  <option value="">Select Category</option>
                  <option value="General">General</option>
                  <option value="OBC">OBC</option>
                  <option value="SC">SC</option>
                  <option value="ST">ST</option>
                  <option value="EWS">EWS</option>
                  <option value="Minority">Minority</option>
                </select>
              ) : (
                <input type="text" value={formData.category || '—'} className="student-details-input" readOnly />
              )}
            </div>

            <div className="student-details-group">
              <label className="student-details-label">Religion</label>
              <input type="text" name="religion" value={formData.religion} onChange={handleChange} className="student-details-input" readOnly={!isEditing} />
            </div>

          </div>
        </div>

        {/* ── SECTION: Family Details ── */}
        <div className="student-details-section">
          <h3 className="student-details-section-title"><FaUserTie /> Family Details</h3>
          <div className="student-details-grid student-details-grid-4">
            <div className="student-details-group">
              <label className="student-details-label">Father's Name <span className="student-details-req">*</span></label>
              <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} className="student-details-input" readOnly={!isEditing} required={isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">Father's Occupation</label>
              <input type="text" name="fatherOccupation" value={formData.fatherOccupation} onChange={handleChange} className="student-details-input" readOnly={!isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">Father's Contact <span className="student-details-req">*</span></label>
              <input type="text" name="fatherContact" value={formData.fatherContact} onChange={handleChange} placeholder="Father's mobile number" className="student-details-input" readOnly={!isEditing} required={isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">Alternative Contact</label>
              <input type="text" name="alternativeContact" value={formData.alternativeContact} onChange={handleChange} placeholder="Alternative number" className="student-details-input" readOnly={!isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">Mother's Name <span className="student-details-req">*</span></label>
              <input type="text" name="motherName" value={formData.motherName} onChange={handleChange} className="student-details-input" readOnly={!isEditing} required={isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">Mother's Occupation</label>
              <input type="text" name="motherOccupation" value={formData.motherOccupation} onChange={handleChange} className="student-details-input" readOnly={!isEditing} />
            </div>
          </div>
        </div>

        {/* ── SECTION: Contact Details ── */}
        <div className="student-details-section">
          <h3 className="student-details-section-title"><FaPhoneAlt /> Contact Information</h3>
          <div className="student-details-grid student-details-grid-4">
            <div className="student-details-group">
              <label className="student-details-label">Contact Number <span className="student-details-req">*</span></label>
              <input type="text" name="contact" value={formData.contact} onChange={handleChange} className="student-details-input" readOnly={!isEditing} required={isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">Email Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} className="student-details-input" readOnly={!isEditing} />
            </div>
            <div className="student-details-group student-details-full-width">
              <label className="student-details-label">Address <span className="student-details-req">*</span></label>
              <input type="text" name="address" value={formData.address} onChange={handleChange} className="student-details-input" readOnly={!isEditing} required={isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">City</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} className="student-details-input" readOnly={!isEditing} />
            </div>
            <div className="student-details-group">
              <label className="student-details-label">State</label>
              {isEditing ? (
                <StateSelect
                  value={formData.state}
                  onChange={v => setFormData(prev => ({ ...prev, state: v }))}
                />
              ) : (
                <input type="text" value={formData.state || '—'} className="student-details-input" readOnly />
              )}
            </div>
            <div className="student-details-group">
              <label className="student-details-label">Country</label>
              <input type="text" className="student-details-input" value="India" readOnly disabled />
            </div>
          </div>
        </div>

        {/* ── SECTION: Education Document ── */}
        <div className="student-details-section">
          <h3 className="student-details-section-title"><FaSchool /> Education Document</h3>
          <div className="student-details-grid student-details-grid-4">
            <div className="student-details-group">
              <label className="student-details-label">Education Document (PDF only)</label>
              {!isEditing && existingDocPath ? (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input type="text" value={educationDocName || '—'} className="student-details-input" readOnly style={{ flex: 1 }} />
                  <button type="button" onClick={() => window.open(`${API_BASE}/${existingDocPath}`, '_blank')} className="student-details-edit-btn" style={{ padding: '8px 16px', fontSize: '14px' }}>View Document</button>
                </div>
              ) : isEditing ? (
                <>
                  <input type="file" accept="application/pdf" onChange={handleEducationDocChange} className="student-details-input" />
                  {existingDocPath && <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>Current: {educationDocName}</small>}
                </>
              ) : (
                <input type="text" value="—" className="student-details-input" readOnly />
              )}
            </div>
          </div>
        </div>



        {/* ── Action Bar (Only visible when editing) ── */}
        {isEditing && (
          <div className="student-details-action-bar">
            <button type="submit" className="student-details-btn-submit" disabled={saving}>
              {saving ? 'Saving Changes...' : <><FaSave /> Save Information</>}
            </button>
          </div>
        )}

      </form>
    </div>
  );
};

export default StudentDetails;